<script lang="ts">
    import { createEventDispatcher, onMount } from "svelte"
    import { onChange } from "../formComponents/helper"
    import Input from "../formComponents/Input.svelte"
    import ToggleButton from "../ToggleButton.svelte"
    import Calendar from "../formComponents/Calendar.svelte"
    import Select from "../../taskSteps/inputfields/Select.svelte"
    import TowingForm from "./TowingForm.svelte"
    import { login } from "../../store"

    export let task: BaseTask
    export let style = ""
    export let isDashboard: boolean = false
    let selected: 0 | 1 = task.taskType == "oilSpillRemoval" ? 0 : 1
    let lastTaskType: BaseTask["taskType"] = task.taskType
    let lastDispatchedTaskType: BaseTask["taskType"] = task.taskType
    type TaskUpdatePayload = { patch: Partial<BaseTask>; valueChanged?: boolean }
    const dispatch = createEventDispatcher<{
        blur: TaskUpdatePayload
        change: TaskUpdatePayload
        taskTypeChange: { taskType: BaseTask["taskType"] }
    }>()
    $: options = {
        endpoint: "user",
        params: {
            sort: "username",
            filter: {
                $or: [
                    {
                        roles: "admin",
                    },
                    {
                        additionalPermissions: selected == 1 ? "editTowingOrders" : "editOilSpillOrders",
                    },
                ],
            },
        },
        mapping: {
            id: "id",
            name: "username",
        },
    }

    function emitMetaBlur(valueChanged: boolean) {
        dispatch("blur", { patch: { meta: task.meta }, valueChanged })
    }

    function emitGeneralDetailsBlur(valueChanged: boolean) {
        dispatch("blur", { patch: { generalDetails: task.generalDetails }, valueChanged })
    }

    function emitMetaChange() {
        dispatch("change", { patch: { meta: task.meta } })
    }

    function emitGeneralDetailsChange() {
        dispatch("change", { patch: { generalDetails: task.generalDetails } })
    }

    let createdAt = new Date()
    onMount(() => {
        if (task.insertTime) {
            createdAt = new Date(task.insertTime)
            return
        }

        const interval = setInterval(() => {
            createdAt = new Date()
        }, 1000)
        return () => clearInterval(interval)
    })

    $: if (task && task.taskType !== lastTaskType) {
        selected = task.taskType === "oilSpillRemoval" ? 0 : 1
        lastTaskType = task.taskType
        lastDispatchedTaskType = task.taskType
    }

    $: {
        const currentType = selected === 0 ? "oilSpillRemoval" : "towing"
        if (currentType !== lastDispatchedTaskType) {
            lastDispatchedTaskType = currentType
            dispatch("taskTypeChange", { taskType: currentType })
        }
    }
</script>

<div
    class="inner-card-wrapper"
    style="{style}"
>
    <Input
        placeholder="Ausführendes Unternehmen"
        id="executingCompany"
        onChange="{onChange}"
        bind:value="{task.meta.executingCompany}"
        onBlur="{(_, valueChanged) => emitMetaBlur(valueChanged)}"
    />
    <Input
        placeholder="Angelegt von (Automatisch Vergeben)"
        id="createdBy"
        onChange="{onChange}"
        value="{task.meta.createdBy.name}"
        disabled="{true}"
        onBlur="{(_, valueChanged) => emitMetaBlur(valueChanged)}"
    />
    <Calendar
        id="createdAt"
        placeholder="Angelegt am (Automatisch vergeben)"
        dateValue="{createdAt}"
        editMode="{false}"
    />

    <div class="grid">
        <div
            style="height: 60px;"
            class="col-6"
        >
            <Select
                field="{{
                    name: 'id',
                    label: 'Kontaktperson',
                    widget: 'select',
                    pathInDb: 'id',
                    options: {
                        endpoint: 'user',
                        params: {
                            sort: 'username',
                        },
                        mapping: {
                            id: 'id',
                            name: 'username',
                        },
                    },
                }}"
                bind:value="{task.meta.contactPerson.id}"
                on:valueChanged="{() => emitMetaChange()}"
            />
        </div>
        {#if $login?.user?.roles.includes("admin") || ($login?.user?.additionalPermissions || []).includes("createOrders") || ($login?.user?.additionalPermissions || []).includes("assignPeople")}
            <div
                style="height: 60px;"
                class="col-6"
            >
                {#key selected}
                    <Select
                        field="{{
                            name: 'id',
                            label: 'Zugewiesene Personen',
                            widget: 'select[]',
                            pathInDb: 'id',
                            options: options,
                        }}"
                        bind:value="{task.meta.assignedUsers}"
                        on:valueChanged="{() => emitMetaChange()}"
                    />
                {/key}
            </div>
        {/if}
    </div>
    {#if selected == 1 && isDashboard}
        <div class="grid">
            <div
                style="height: 60px;"
                class="col-6"
            >
                <Input
                    placeholder="Schadenort"
                    id="nameOverride"
                    onChange="{onChange}"
                    bind:value="{task.generalDetails.location.overriddenName}"
                />
            </div>
            <div
                style="height: 60px;"
                class="col-6"
            >
                <Input
                    placeholder="Kurzbeschreibung"
                    id="shortDescription"
                    onChange="{onChange}"
                    bind:value="{task.generalDetails.shortDescription}"
                    onBlur="{(_, valueChanged) => emitGeneralDetailsBlur(valueChanged)}"
                />
            </div>
        </div>
    {:else}
        <Input
            placeholder="Kurzbeschreibung"
            id="shortDescription"
            onChange="{onChange}"
            bind:value="{task.generalDetails.shortDescription}"
            onBlur="{(_, valueChanged) => emitGeneralDetailsBlur(valueChanged)}"
        />
    {/if}
    {#if !isDashboard || selected == 0}
        <Input
            placeholder="Beschreibung"
            id="description"
            type="textarea"
            onChange="{onChange}"
            smallTextarea="{true}"
            bind:value="{task.generalDetails.description}"
            onBlur="{(_, valueChanged) => emitGeneralDetailsBlur(valueChanged)}"
        />
    {/if}
    {#if selected == 1}
        <TowingForm
            bind:generalDetails="{task.generalDetails}"
            on:change="{() => emitGeneralDetailsChange()}"
            on:blur="{(event) => emitGeneralDetailsBlur(event.detail?.valueChanged ?? true)}"
        />
    {/if}

    <ToggleButton
        icon1="{selected == 0 ? 'solar_waterdrops-outline-white.svg' : 'solar_waterdrops-outline.svg'}"
        icon2="{selected == 1 ? 'hugeicons_tow-truck-white.svg' : 'hugeicons_tow-truck.svg'}"
        title1="{'ÖLWEHREINSATZ'}"
        title2="{'ABSCHLEPPEINSATZ'}"
        bind:selected="{selected}"
    />
</div>

<style lang="less">
    .inner-card-wrapper {
        display: flex;
        flex-direction: column;
        padding: 1.2rem;
        display: flex;
        flex-direction: column;
        gap: 1.2rem;
        width: 100%;
    }
</style>
